/**
 * The rotating word at the end of the homepage headline: "The Free Research Hub for [Biohackers]". The owner brought it
 * back on 2026-10-24 (it left with the 2026-09-30 rebuild). Rules, from CLAUDE.md section 6:
 *
 *  - The static shell in index.html and React's first frame both show "Biohackers", and the headline's text in the DOM
 *    is exactly "The Free Research Hub for Biohackers" until the first change. The other words are never in the DOM
 *    text before then, so crawlers and prerender snapshots read the one headline.
 *  - No layout shift. The slot is a one-cell grid. Its ::before draws the widest phrase, "Performance Optimizers"
 *    (11.77em in Inter Black, 11.38em in the fallback; data-sizer), invisibly in the same cell, so the cell always
 *    holds the largest word: one line from 640px, two lines on a phone, where it wraps. .home-title sizes the headline
 *    so that phrase is never near its wrap point in either font. Words change inside the cell; nothing around moves.
 *  - Never for crawlers: no rotation when navigator.webdriver is set or the user agent looks like a bot, a headless
 *    browser or Lighthouse. None under prefers-reduced-motion. The first change comes 2.8 seconds after the page
 *    starts, then one every 2.8 seconds, paused while the tab is hidden.
 *  - Transform and opacity only: the new word rises in over 300ms while the old one rises out.
 *  - The heading's accessible name stays "The Free Research Hub for Biohackers": once rotation starts the moving words
 *    are aria-hidden and a visually hidden "Biohackers" takes their place.
 */
import { useEffect, useRef, useState } from "react";

export const ROTATING_WORDS = ["Biohackers", "Peptide Researchers", "Longevity Seekers", "Nootropic Users", "Performance Optimizers"];

/** The widest phrase, measured in both fonts (see above). The slot reserves its size. */
export const ROTATING_SIZER = "Performance Optimizers";

/** One word every 2.8 seconds; the first change 2.8 seconds in. */
export const ROTATE_EVERY_MS = 2800;

const BOT_UA = /bot|crawl|spider|slurp|headless|lighthouse|prerender|externalhit|externalagent|preview/i;

/** Whether this visitor gets the rotation: a person's browser that has not asked for less motion. */
export function rotationAllowed({ webdriver, userAgent, reducedMotion }: { webdriver: boolean; userAgent: string; reducedMotion: boolean }): boolean {
  return !webdriver && !reducedMotion && !BOT_UA.test(userAgent);
}

function allowedHere(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  return rotationAllowed({
    webdriver: navigator.webdriver === true,
    userAgent: navigator.userAgent || "",
    reducedMotion: !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  });
}

type Rotation = { index: number; leaving: number | null; tick: number };

export default function RotatingWord() {
  const [state, setState] = useState<Rotation>({ index: 0, leaving: null, tick: 0 });
  const indexRef = useRef(0);

  useEffect(() => {
    if (!allowedHere()) return;
    const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    let timer: number | undefined;
    let stopped = false;

    const next = () => {
      if (stopped || document.hidden) return;
      const leaving = indexRef.current;
      const index = (leaving + 1) % ROTATING_WORDS.length;
      indexRef.current = index;
      setState((s) => ({ index, leaving, tick: s.tick + 1 }));
      timer = window.setTimeout(next, ROTATE_EVERY_MS);
    };
    // Counted from when the page started loading, so a slow load does not hold "Biohackers" for long, and a snapshot
    // taken at load never sees another word.
    const firstWait = Math.max(ROTATE_EVERY_MS - performance.now(), 600);
    timer = window.setTimeout(next, firstWait);

    const onVisibility = () => {
      window.clearTimeout(timer);
      if (!document.hidden && !stopped) timer = window.setTimeout(next, ROTATE_EVERY_MS);
    };
    const onMotion = () => {
      if (motion?.matches) {
        stopped = true;
        window.clearTimeout(timer);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    motion?.addEventListener?.("change", onMotion);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      motion?.removeEventListener?.("change", onMotion);
    };
  }, []);

  const { index, leaving, tick } = state;
  const started = tick > 0;
  return (
    <span className="home-slot" data-sizer={ROTATING_SIZER}>
      {leaving !== null && (
        <span
          key={`out${tick}`}
          aria-hidden="true"
          className="home-slot-item home-word-out"
          onAnimationEnd={() => setState((s) => (s.tick === tick ? { ...s, leaving: null } : s))}
        >
          <span className="home-word">{ROTATING_WORDS[leaving]}</span>
        </span>
      )}
      <span key={`in${tick}`} aria-hidden={started ? "true" : undefined} className={started ? "home-slot-item home-word-in" : "home-slot-item"}>
        <span className="home-word">{ROTATING_WORDS[index]}</span>
      </span>
      {started && <span className="sr-only">{ROTATING_WORDS[0]}</span>}
    </span>
  );
}
